import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams }       from "react-router-dom";
import { useAuth }               from "@context/AuthContext";
import cache                     from "@utils/cache";
import SearchBar          from "@features/search/SearchBar";
import SearchFilterPanel  from "@features/search/SearchFilterPanel";
import SearchResultsPanel from "@features/search/SearchResultsPanel";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";


const COLORS = ["#2563EB","#16A34A","#F59E0B","#EC4899","#06B6D4","#7C3AED","#DC2626","#F97316","#0EA5E9","#16A34A"];

const emptyFilters = { jobRoles: [], skills: [], certifications: [], languages: [], location: [], availability: "" };

// Map API row → CandidateCard shape
const mapResult = (r, idx, ownFirm) => {
  const isOwn    = ownFirm && ownFirm === r.firm_id;
  const name     = isOwn ? (r.resource_name || "Unknown") : "Confidential";
  const skills   = r.skill_name ? r.skill_name.split(",").map(s => s.trim()).filter(Boolean) : [];
  const avail    = Number(r.availability || 0);
  const initials = name.replace("Confidential", "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const location = [r.city_name, r.state_name, r.country_name].filter(Boolean).join(", ");
  return { id: r.user_slug || r.id, name, role: r.user_profile_role || "", location, availability: avail, skills, exp: r.experience || "", initials, color: COLORS[idx % COLORS.length] };
};

export default function SearchPage() {
  const [searchParams]   = useSearchParams();
  const { userSlug }     = useAuth();

  // Read keyword/availability from URL (when coming from Recent Searches, or the Availability Breakdown dashboard widget)
  const urlKeyword      = searchParams.get("keyword") || searchParams.get("q") || "";
  const urlAvailability = searchParams.get("availability") || "";

  const [keyword,      setKeyword]      = useState(typeof urlKeyword === "string" ? urlKeyword : "");
  const [filters,      setFilters]      = useState(emptyFilters);
  const [results,      setResults]      = useState([]);
  const [raw,          setRaw]          = useState([]);
  const [ownFirm,      setOwnFirm]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const didMount = useRef(false);

  // Cache key per user
  const CACHE_KEY = `search_state_${userSlug}`;

  const runSearch = useCallback(async (pg = 1, kw = keyword, fil = filters) => {
    if (!userSlug) return;
    setLoading(true);
    setPage(pg);

    const params = {
      user_slug:      userSlug,
      keyword:        (typeof kw === "string" ? kw : "").trim(),
      jobRoles:       fil.jobRoles,
      skills:         fil.skills,
      certifications: fil.certifications,
      languages:      fil.languages,
      location:       fil.location,
      availability:   fil.availability,
      page:           pg,
      per_page:       10,
    };

    try {
      const fd = new FormData();
      fd.append("params", JSON.stringify(params));
      const res  = await axios.post(`${API_BASE}get-search-result-v2`, fd);
      const data = res.data;

      if (data?.status === 1 && data.result?.data?.length > 0) {
        const firm = data.own_firm ?? null;
        setOwnFirm(firm);
        setRaw(data.result.data);
        const mapped = data.result.data.map((r, i) => mapResult(r, i, firm));
        setResults(mapped);
        setTotalPages(data.result.total_pages || 1);
        setTotalResults(data.result.total || 0);

        // Cache current state
        cache.set(CACHE_KEY, { keyword: kw, filters: fil, results: mapped, raw: data.result.data, ownFirm: firm, page: pg, totalPages: data.result.total_pages || 1, totalResults: data.result.total || 0 });
      } else {
        setResults([]); setRaw([]);
        setTotalPages(1); setTotalResults(0);
        cache.clear(CACHE_KEY);
      }
    } catch {
      setResults([]); setRaw([]);
    }
    setLoading(false);
  }, [userSlug, keyword, filters]);

  useEffect(() => {
    if (!userSlug || didMount.current) return;
    didMount.current = true;

    // 1. Coming from Recent Searches (keyword) or the Availability Breakdown
    //    dashboard widget (availability filter) — run search with whichever
    //    (or both) arrived via URL
    if (urlKeyword || urlAvailability) {
      const initialFilters = { ...emptyFilters, availability: urlAvailability };
      setKeyword(urlKeyword);
      setFilters(initialFilters);
      runSearch(1, urlKeyword, initialFilters);
      return;
    }

    // 2. Returning to page — restore cached state instantly
    const cached = cache.get(CACHE_KEY);
    if (cached) {
      setKeyword(cached.keyword || "");
      setFilters({ ...emptyFilters, ...(cached.filters || {}) });
      setResults(cached.results || []);
      setRaw(cached.raw || []);
      setOwnFirm(cached.ownFirm || null);
      setPage(cached.page || 1);
      setTotalPages(cached.totalPages || 1);
      setTotalResults(cached.totalResults || 0);
      return;
    }

    // 3. Fresh open — auto search with empty keyword to show all results
    runSearch(1, "", emptyFilters);
  }, [userSlug]);

  const handleReset = () => {
    setFilters(emptyFilters);
    setResults([]); setRaw([]);
    setTotalResults(0); setTotalPages(1);
    cache.clear(CACHE_KEY);
  };

  return (
    <div>
      {/* Search bar */}
      <div style={{ marginBottom: 16 }}>
        <SearchBar value={keyword} onChange={setKeyword} onSearch={(kw) => { const q = kw !== undefined ? kw : keyword; if (kw !== undefined) setKeyword(typeof kw === "string" ? kw : ""); runSearch(1, q); }} userSlug={userSlug} />
      </div>

      {/* Mobile filter toggle */}
      <button className="d-lg-none fc-btn fc-btn-sm fc-btn-ghost mb-3" onClick={() => setMobileOpen(o => !o)}>
        <i className="fa-solid fa-sliders me-2" style={{ fontSize: 12 }} />
        {mobileOpen ? "Hide Filters" : "Show Filters"}
        {([...filters.jobRoles,...filters.skills,...filters.certifications,...filters.languages,...filters.location].length > 0 || filters.availability) && (
          <span className="fc-filter-count ms-2">
            {[filters.jobRoles.length>0, filters.skills.length>0, filters.certifications.length>0, filters.languages.length>0, filters.location.length>0, !!filters.availability].filter(Boolean).length}
          </span>
        )}
      </button>

      <div className="fc-search-layout">
        <div className={`fc-search-filter-col${mobileOpen ? " mobile-open" : ""}`}>
          <SearchFilterPanel filters={filters} onChange={f => setFilters(f)} onReset={handleReset} totalResults={totalResults} />
        </div>

        <div className="fc-search-results-col" style={{ paddingBottom:20 }}>
          {loading ? (
            <div className="d-flex flex-column gap-2">
              {[1,2,3,4].map(i => (
                <div key={i} className="fc-card" style={{ padding: 16 }}>
                  <div className="d-flex gap-3 align-items-center">
                    <div style={{ width:46, height:46, borderRadius:9, background:"var(--fc-border-light)" }} />
                    <div style={{ flex:1 }}>
                      <div style={{ height:13, background:"var(--fc-border-light)", borderRadius:6, width:"40%", marginBottom:8 }} />
                      <div style={{ height:11, background:"var(--fc-border-light)", borderRadius:6, width:"25%" }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <SearchResultsPanel
              results={results}
              rawResults={raw}
              page={page}
              totalPages={totalPages}
              totalResults={totalResults}
              onPageChange={(p) => runSearch(p)}
              ownFirm={ownFirm}
            />
          )}
        </div>
      </div>
    </div>
  );
}